<template>
	<section v-if="videoList.length" class="recommend-list">
		<ul class="recommend-list__items">
			<li
				v-for="(item, index) in videoList"
				:key="item.id"
				:ref="el => bindRecommendRef(el, item, index)"
				class="recommend-list__item"
				@click="handleToDetail(item, index)"
			>
				<div class="recommend-list__thumb">
					<img :src="item.cover" :alt="item.title" class="recommend-list__cover" />
					<img :src="playIcon" alt="" class="recommend-list__play" aria-hidden="true" />
					<span
						v-if="item.media_second_str || item.media_second != null"
						class="recommend-list__duration"
					>
						{{ item.media_second_str || formatDuration(item.media_second) }}
					</span>
				</div>
				<p class="recommend-list__title">{{ item.title }}</p>
				<div class="recommend-list__meta">
					<img :src="videoIcon" alt="" class="recommend-list__meta-icon" />
					<span>{{ formatViewCount(item.view_count) }}</span>
				</div>
			</li>
		</ul>
	</section>
	<p v-else-if="!loading" class="recommend-list__empty">{{ t("square.detail.emptyRecommend") }}</p>
</template>

<script setup>
import { computed, inject } from "vue";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { getSquareRecommend, playContent } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import {
	buildRelatedItemParams,
	useDetailListImpression,
} from "../../../composables/useSquareDetailTracking";
import videoIcon from "../../../images/vedio.png";
import playIcon from "../../../../square/images/vedio2.png";

dayjs.extend(duration);

const MAX_VIDEO_COUNT = 7;

const props = defineProps({
	squareInfo: {
		type: Object,
		default: () => ({}),
	},
	squareId: {
		type: String,
		default: "",
	},
});

const { t, locale } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const { bindItemRef: bindRecommendRef } = useDetailListImpression({
	logDetailEvent,
	impressionEvent: "media_related_content_impression",
	getItemParams: (item, index) => buildRelatedItemParams(props.squareInfo, item, index + 1),
});

const { data: recommendData, pending: loading } = await useLazyAsyncData(
	() => `square-detail-m-recommend-${props.squareId}`,
	async () => {
		const info = props.squareInfo;
		const squareId = props.squareId || info?.id;
		if (!squareId || !info?.uid) return [];

		const res = await getSquareRecommend({
			square_id: String(squareId),
			uid: String(info.uid),
			class_id: String(info.class_id ?? 0),
		});

		const list = res?.data?.horiz_video;
		return Array.isArray(list) ? list.slice(0, MAX_VIDEO_COUNT) : [];
	},
	{
		default: () => [],
		watch: [() => props.squareInfo?.id, () => props.squareId],
	}
);

const videoList = computed(() => recommendData.value || []);

const formatDuration = seconds => {
	const value = Number(seconds) || 0;
	if (!value) return "";
	return dayjs.duration(value, "seconds").format("mm:ss");
};

const formatViewCount = count => {
	const value = Number(count) || 0;
	if (value >= 10000) {
		if (locale.value === "en") {
			return t("square.views.count", { count: `${(value / 1000).toFixed(1)}K` });
		}
		return t("square.views.tenThousand", { count: (value / 10000).toFixed(1) });
	}
	return t("square.views.count", { count: value });
};

const handleToDetail = async (item, index) => {
	if (!item?.id) return;
	if (logDetailEvent) {
		logDetailEvent(
			"media_related_content_click",
			buildRelatedItemParams(props.squareInfo, item, index + 1)
		);
	}
	await navigateTo(`/seer/square-detail/${item.id}`);
	await playContent({ square_id: item.id });
};
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.recommend-list {
	padding: 0 30px 40px;

	&__items {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__item {
		margin-bottom: 32px;
		cursor: pointer;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__thumb {
		position: relative;
		width: 100%;
		border-radius: 16px;
		overflow: hidden;
		background: #f0f0f0;
	}

	&__cover {
		display: block;
		width: 100%;
		height: 392px;
		object-fit: cover;
	}

	&__play {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		width: 80px;
		height: 80px;
		object-fit: contain;
		pointer-events: none;
	}

	&__duration {
		position: absolute;
		right: 16px;
		bottom: 16px;
		padding: 4px 12px;
		border-radius: 6px;
		background: rgba(0, 0, 0, 0.6);
		color: #ffffff;
		font-size: 22px;
		line-height: 1.4;
	}

	&__title {
		margin: 20px 0 12px;
		font-size: 30px;
		font-weight: 600;
		color: #1f2329;
		line-height: 1.5;
		@include line-clamp(2);
	}

	&__meta {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 24px;
		color: #999999;
	}

	&__meta-icon {
		width: 28px;
		height: 28px;
		object-fit: contain;
		opacity: 0.6;
	}

	&__empty {
		margin: 0;
		padding: 40px 30px 60px;
		text-align: center;
		font-size: 28px;
		color: #999999;
	}
}
</style>
